/**
 * Lightweight in-memory cache + deduplication for Supabase requests.
 *
 * - dedupeRequest: collapses concurrent identical calls into one in-flight promise
 * - cachedRequest: TTL-based cache so repeated calls within the window return immediately
 */

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry<any>>();
const inFlight = new Map<string, Promise<any>>();

/**
 * Execute `fn` at most once per `ttlMs` window for a given `key`.
 * Concurrent callers within the same window share the single in-flight promise.
 */
export async function cachedRequest<T>(
  key: string,
  fn: () => Promise<T>,
  ttlMs = 60_000
): Promise<T> {
  const now = Date.now();

  // 1. Return cached value if still fresh
  const entry = cache.get(key);
  if (entry && entry.expiresAt > now) {
    return entry.value as T;
  }

  // 2. Deduplicate concurrent calls for the same key
  const existing = inFlight.get(key);
  if (existing) return existing as Promise<T>;

  // 3. Execute and cache
  const promise = fn()
    .then((result) => {
      cache.set(key, { value: result, expiresAt: Date.now() + ttlMs });
      inFlight.delete(key);
      return result;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });

  inFlight.set(key, promise);
  return promise;
}

/** Invalidate a specific cache key (e.g. on sign-out). */
export function invalidateCache(key: string) {
  cache.delete(key);
  // Don't cancel in-flight — just let it complete and then the next call will re-fetch
}

/** Invalidate all cache entries whose key starts with a prefix. */
export function invalidateCachePrefix(prefix: string) {
  for (const k of cache.keys()) {
    if (k.startsWith(prefix)) cache.delete(k);
  }
}
